export interface VideoFilenameMetadata {
  readonly id: string;
  readonly title: string;
  readonly sourceFileName?: string | undefined;
  readonly preset: string;
  readonly createdAt?: string | undefined;
  readonly downloadFilename?: string | null | undefined;
}

const ASPECTS = new Map([
  ['landscape-16x9', '16x9'], ['social-4x5', '4x5'], ['vertical-9x16', '9x16'],
]);

/** Produces one bounded Unicode filename for every private video download route. */
export function videoDownloadFilename(video: VideoFilenameMetadata): string {
  if (video.downloadFilename) {
    if (/[\x00-\x1f\x7f"\\/]/u.test(video.downloadFilename) || !video.downloadFilename.endsWith('.mp4')) throw new Error('Invalid saved export filename.');
    return video.downloadFilename;
  }
  const title = filenamePart(video.title, 80) || 'Trace';
  const aspect = ASPECTS.get(video.preset);
  const date = video.createdAt ? new Date(video.createdAt) : undefined;
  const timestamp = date && Number.isFinite(date.getTime())
    ? date.toISOString().slice(2, 19).replace(/[-:]/gu, '').replace('T', '-') + 'Z'
    : `Undated-${filenamePart(video.id, 36) || 'Unknown'}`;
  return `${title}_${aspect ?? 'UnknownAspect'}_${timestamp}.mp4`;
}

/** Joins words in PascalCase while preserving Unicode letters and complete emoji sequences. */
export function filenamePart(value: string, limit: number): string {
  const name = value.normalize('NFC')
    .split(/[^\p{L}\p{N}\p{M}\p{Extended_Pictographic}\p{Regional_Indicator}\p{Emoji_Modifier}\u200d\ufe0f\s]+/u)
    .map(part => part.trim().split(/\s+/u).filter(Boolean)
      .map(word => {const [first,...rest]=Array.from(word);return first!.toUpperCase()+rest.join('');}).join(''))
    .filter(Boolean).join('_');
  let result='',bytes=0;
  for(const {segment} of new Intl.Segmenter(undefined,{granularity:'grapheme'}).segment(name)) {
    const length=new TextEncoder().encode(segment).length;
    if(bytes+length>limit)break;
    result+=segment;bytes+=length;
  }
  return result.replace(/_+$/u, '');
}

/** HTTP filenames use an ASCII fallback plus the UTF-8 parameter defined by RFC 6266/8187. */
export function videoDownloadDisposition(video: VideoFilenameMetadata): string {
  const filename=videoDownloadFilename(video);
  if(/^[\x20-\x7e]+$/u.test(filename))return `attachment; filename="${filename}"`;
  const fallback=filename.replace(/[^a-zA-Z0-9_.]+/gu,'_');
  const encoded=encodeURIComponent(filename).replace(/['()*]/gu,char=>`%${char.charCodeAt(0).toString(16).toUpperCase()}`);
  return `attachment; filename="${fallback}"; filename*=UTF-8''${encoded}`;
}

/** Single-coordinator stores reserve this value before yielding to another submission. */
export function availableVideoDownloadFilename(video: VideoFilenameMetadata, filenames: Iterable<string | undefined>): string {
  let filename = videoDownloadFilename(video);
  const base = filename.slice(0, -4), taken = new Set(filenames);
  for (let sequence = 2; taken.has(filename); sequence++) filename = `${base}-${sequence}.mp4`;
  return filename;
}
